import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, MapPin, Menu, X, Wrench, Phone, Coffee, LayoutGrid, Sparkles, Cloud, FileText, Users, ClipboardList, Shield, CheckCircle, Bookmark, User, Calendar, Train, Radio, Play, Loader2, Music, CheckSquare, Zap } from 'lucide-react';
import { seedDemo } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Wordmark from '../components/Wordmark';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  phone: Phone,
  wrench: Wrench,
  coffee: Coffee,
  layout: LayoutGrid,
  cloud: Cloud,
  'file-text': FileText,
  users: Users,
  clipboard: ClipboardList,
  shield: Shield,
  'check-circle': CheckCircle,
  'map-pin': MapPin,
  calendar: Calendar,
  bookmark: Bookmark,
  user: User,
};

// ─────────────────────────────────────────────────────────────────────────────
// HERO CAROUSEL — Three pillars of STAGE8
// ─────────────────────────────────────────────────────────────────────────────
const HERO_SLIDES = [
  {
    id: 'gigs',
    label: 'GIGS',
    headline: 'FIND YOUR\nNEXT GIG',
    supporting: 'Browse open requests from venues across the Netherlands. Real gigs, real pay, clear logistics.',
    cta: 'BROWSE GIGS',
    ctaLink: '/requests',
    image: '/images/hero-gigs.jpg',
    imageAlt: 'Empty stage with drum kit, keyboards, guitars and microphones under warm stage lights',
    imagePosition: 'center 65%',
  },
  {
    id: 'musicians',
    label: 'MUSICIANS',
    headline: 'BOOK LOCAL\nMUSICIANS',
    supporting: 'Verified talent with clear rates, gear lists, and reliability scores. No surprises on show day.',
    cta: 'FIND MUSICIANS',
    ctaLink: '/musicians',
    image: '/images/hero-musicians.jpg',
    imageAlt: 'Guitarist performing live on stage with colorful stage lights',
    imagePosition: '35% center',
  },
  {
    id: 'tech-crew',
    label: 'CREW',
    headline: 'HIRE\nTECH CREW',
    supporting: 'Sound engineers, lighting techs, and stage hands ready for your next event. Gear-verified.',
    cta: 'FIND CREW',
    ctaLink: '/tech',
    image: '/images/hero-tech-crew.jpg',
    imageAlt: 'Tech crew working at lighting and audio control desk during live production',
    imagePosition: 'center 65%',
  },
];

function FaderCapSVG({ x, trackTop, capW, capH }) {
  const W = capW, H = capH;
  const r = 3;
  // Centered at origin — rotate(90deg) pivots around cap center
  const hw = W / 2, hh = H / 2;
  const iHalf = Math.round(H * 0.12); // half of center indent from center

  const topRidgeYs = Array.from({ length: 5 }, (_, i) =>
    -hh + 5 + i * ((hh - iHalf - 10) / 4)
  );
  const botRidgeYs = Array.from({ length: 5 }, (_, i) =>
    iHalf + 4 + i * ((hh - iHalf - 9) / 4)
  );

  return (
    <g
      style={{
        // CSS transform animates smoothly; rotate(90deg) makes portrait cap appear landscape
        transform: `translate(${x}px, ${trackTop}px) rotate(90deg)`,
        transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
        cursor: 'grab',
        // CSS drop-shadow applies in screen space after rotation — shadow falls downward correctly
        filter: 'drop-shadow(0px 6px 10px rgba(0,0,0,0.90))',
      }}
    >
      <defs>
        <linearGradient id="fcBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#EAC870" />
          <stop offset="22%"  stopColor="#D4A040" />
          <stop offset="44%"  stopColor="#B07828" />
          <stop offset="50%"  stopColor="#985E18" />
          <stop offset="56%"  stopColor="#B07828" />
          <stop offset="78%"  stopColor="#CC9838" />
          <stop offset="100%" stopColor="#884E10" />
        </linearGradient>
        <linearGradient id="fcSheen" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.22)" />
          <stop offset="35%"  stopColor="rgba(255,255,255,0.05)" />
          <stop offset="65%"  stopColor="rgba(0,0,0,0.03)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.22)" />
        </linearGradient>
        <clipPath id="fcClip">
          <rect x={-hw} y={-hh} width={W} height={H} rx={r} ry={r} />
        </clipPath>
      </defs>

      {/* Body */}
      <rect x={-hw} y={-hh} width={W} height={H} rx={r} ry={r} fill="url(#fcBody)" />
      {/* Side sheen */}
      <rect x={-hw} y={-hh} width={W} height={H} rx={r} ry={r} fill="url(#fcSheen)" />

      {/* Top highlight edge */}
      <rect x={-hw+r} y={-hh+0.5} width={W-2*r} height="2" rx="1"
        fill="rgba(255,255,255,0.42)" clipPath="url(#fcClip)" />
      {/* Bottom shadow edge */}
      <rect x={-hw+r} y={hh-2.5} width={W-2*r} height="2.5" rx="1"
        fill="rgba(0,0,0,0.55)" clipPath="url(#fcClip)" />
      {/* Left bevel */}
      <rect x={-hw+0.5} y={-hh+r} width="1.5" height={H-2*r}
        fill="rgba(255,255,255,0.16)" clipPath="url(#fcClip)" />
      {/* Right shadow */}
      <rect x={hw-2} y={-hh+r} width="1.5" height={H-2*r}
        fill="rgba(0,0,0,0.32)" clipPath="url(#fcClip)" />

      {/* Top wing ridges — become vertical lines after 90° rotation */}
      {topRidgeYs.map((y, i) => (
        <g key={`tr${i}`} clipPath="url(#fcClip)">
          <line x1={-hw+5} y1={y} x2={hw-5} y2={y} stroke="rgba(0,0,0,0.55)" strokeWidth="1.2" />
          <line x1={-hw+5} y1={y+1.2} x2={hw-5} y2={y+1.2} stroke="rgba(255,255,255,0.14)" strokeWidth="0.7" />
        </g>
      ))}

      {/* Center indentation — becomes a vertical groove after 90° rotation */}
      <rect x={-hw} y={-iHalf} width={W} height={iHalf*2} fill="rgba(0,0,0,0.30)" clipPath="url(#fcClip)" />
      {/* Top wall shadow */}
      <rect x={-hw} y={-iHalf} width={W} height="3" fill="rgba(0,0,0,0.52)" clipPath="url(#fcClip)" />
      {/* Bottom wall highlight */}
      <rect x={-hw} y={iHalf-2} width={W} height="2" fill="rgba(255,255,255,0.14)" clipPath="url(#fcClip)" />
      {/* Center position notch — becomes a horizontal mark after rotation */}
      <rect x={-hw*0.64} y={-0.75} width={W*0.64} height="1.5" rx="0.5"
        fill="rgba(255,255,255,0.82)" clipPath="url(#fcClip)" />

      {/* Bottom wing ridges */}
      {botRidgeYs.map((y, i) => (
        <g key={`br${i}`} clipPath="url(#fcClip)">
          <line x1={-hw+5} y1={y} x2={hw-5} y2={y} stroke="rgba(0,0,0,0.55)" strokeWidth="1.2" />
          <line x1={-hw+5} y1={y+1.2} x2={hw-5} y2={y+1.2} stroke="rgba(255,255,255,0.14)" strokeWidth="0.7" />
        </g>
      ))}
    </g>
  );
}

function FaderNav({ current, total, slides, goTo, next, prev }) {
  const [tabTouchStart, setTabTouchStart] = useState(null);
  const handleTabTouchStart = (e) => setTabTouchStart(e.touches[0].clientX);
  const handleTabTouchEnd = (e) => {
    if (tabTouchStart === null) return;
    const diff = tabTouchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      e.preventDefault(); // block the synthetic click iOS fires after touchend
      diff > 0 ? next() : prev();
    }
    setTabTouchStart(null);
  };

  const CAP_W = 36;
  const CAP_H = 58;
  const TRAVEL = 240;
  const TRACK_TOP = 34;
  const WRAPPER_H = 96;
  const TICK_ABOVE = 10;
  const TICK_BELOW = 10;
  const capCenter = (current / (total - 1)) * TRAVEL + CAP_W / 2;

  return (
    <div
      role="group"
      aria-label="Carousel navigation"
      style={{ position: 'relative', width: TRAVEL + CAP_W, height: WRAPPER_H, cursor: 'pointer', touchAction: 'pan-x' }}
      onTouchStart={handleTabTouchStart}
      onTouchEnd={handleTabTouchEnd}
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left - CAP_W / 2;
        const idx = Math.round((x / TRAVEL) * (total - 1));
        goTo(Math.max(0, Math.min(total - 1, idx)));
      }}
    >
      {/* SVG layer: track + tick marks + fader cap */}
      <svg
        style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible', pointerEvents: 'none' }}
        width={TRAVEL + CAP_W}
        height={WRAPPER_H}
      >
        {/* Track groove */}
        <rect
          x={CAP_W / 2} y={TRACK_TOP}
          width={TRAVEL} height={4}
          rx="2"
          fill="rgba(10,12,11,0.95)"
        />
        {/* Inset shadow line — top of groove */}
        <line
          x1={CAP_W / 2} y1={TRACK_TOP + 1}
          x2={CAP_W / 2 + TRAVEL} y2={TRACK_TOP + 1}
          stroke="rgba(0,0,0,0.7)" strokeWidth="1"
        />
        {/* Filled portion */}
        <rect
          x={CAP_W / 2} y={TRACK_TOP}
          width={Math.max(0, capCenter - CAP_W / 2)} height={4}
          rx="2"
          fill="url(#trackFill)"
        >
          <animate attributeName="width" dur="0s" />
        </rect>
        <defs>
          <linearGradient id="trackFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(200,146,62,0.35)" />
            <stop offset="100%" stopColor="#C8923E" />
          </linearGradient>
        </defs>

        {/* Interval markers — above AND below the track */}
        {slides.map((s, i) => {
          const sx = (i / (total - 1)) * TRAVEL + CAP_W / 2;
          const isActive = i === current;
          const color = isActive ? '#C8923E' : 'rgba(240,235,224,0.28)';
          return (
            <g key={`mark-${s.id}`}>
              {/* Line above track */}
              <line
                x1={sx} y1={TRACK_TOP - TICK_ABOVE}
                x2={sx} y2={TRACK_TOP}
                stroke={color} strokeWidth="1"
              />
              {/* Line below track */}
              <line
                x1={sx} y1={TRACK_TOP + 4}
                x2={sx} y2={TRACK_TOP + 4 + TICK_BELOW}
                stroke={color} strokeWidth="1"
              />
            </g>
          );
        })}

        {/* Fader cap — drawn on top of track */}
        <FaderCapSVG
          x={capCenter}
          trackTop={TRACK_TOP + 2}
          capW={CAP_W}
          capH={CAP_H}
        />
      </svg>

      {/* Labels */}
      {slides.map((s, i) => {
        const stopLeft = (i / (total - 1)) * TRAVEL + CAP_W / 2;
        const isActive = i === current;
        return (
          <span key={`label-${s.id}`} style={{
            position: 'absolute',
            left: stopLeft,
            top: TRACK_TOP + 4 + TICK_BELOW + 10,
            transform: 'translateX(-50%)',
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 11,
            letterSpacing: '0.06em',
            color: isActive ? '#C8923E' : 'rgba(240,235,224,0.22)',
            transition: 'color 0.3s',
            userSelect: 'none',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}>
            {s.label}
          </span>
        );
      })}

      {/* Accessible button targets */}
      {slides.map((s, i) => (
        <button
          key={s.id}
          onClick={(e) => { e.stopPropagation(); goTo(i); }}
          aria-label={`Go to ${s.id} slide`}
          data-testid={`hero-dot-${s.id}`}
          style={{
            position: 'absolute',
            left: (i / (total - 1)) * TRAVEL + CAP_W / 2,
            top: 0,
            transform: 'translateX(-50%)',
            width: 44,
            height: WRAPPER_H,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        />
      ))}
    </div>
  );
}

function HeroCarousel() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  const goTo = useCallback((index) => {
    setCurrent(index);
  }, []);

  const prev = useCallback(() => {
    setCurrent((c) => (c === 0 ? HERO_SLIDES.length - 1 : c - 1));
  }, []);

  const next = useCallback(() => {
    setCurrent((c) => (c === HERO_SLIDES.length - 1 ? 0 : c + 1));
  }, []);

  // Swipe support
  const [touchStart, setTouchStart] = useState(null);
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchMove = (e) => { e.preventDefault(); };
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? next() : prev();
    }
    setTouchStart(null);
  };

  const slide = HERO_SLIDES[current];

  return (
    <section
      className="relative"
      style={{ background: '#0E1110', overflowX: 'clip' }}
      data-testid="hero-carousel"
    >
      {/* Ambient glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-40px',
          left: '-60px',
          width: '280px',
          height: '280px',
          background: 'radial-gradient(circle, rgba(200, 146, 62, 0.10) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />

      <div className="relative pt-6 sm:pt-12 lg:pt-16 pb-2 sm:pb-4 lg:pb-6">
        <div className="relative">

          {/* Coverflow Image Strip — each image IS the link */}
          <div
            className="coverflow-track"
            style={{ position: 'relative' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {HERO_SLIDES.map((s, i) => {
              const offset = i - current;
              let visualOffset = offset;
              if (offset > 1) visualOffset = offset - HERO_SLIDES.length;
              if (offset < -1) visualOffset = offset + HERO_SLIDES.length;

              const isActive = i === current;
              const isNext = visualOffset === 1;

              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={isActive ? 0 : -1}
                  aria-label={isActive ? `Go to ${s.ctaLink}` : `Select ${s.id}`}
                  className="coverflow-slide group"
                  style={{
                    transform: isActive
                      ? 'translateX(0) scale(1) rotateY(0deg)'
                      : isNext
                      ? 'translateX(var(--peek-offset)) scale(0.88) rotateY(-42deg)'
                      : 'translateX(calc(var(--peek-offset) * -1)) scale(0.88) rotateY(42deg)',
                    opacity: isActive ? 1 : 0.7,
                    zIndex: i === current ? 20 : 10 - Math.abs(visualOffset),
                    filter: isActive ? 'none' : 'brightness(0.55)',
                    pointerEvents: isActive ? 'auto' : 'none',
                    cursor: 'pointer',
                    display: 'block',
                  }}
                  onClick={() => {
                    if (isActive) {
                      navigate(s.ctaLink);
                    } else if (isNext) next();
                    else prev();
                  }}
                  onKeyDown={(e) => {
                    if (!isActive) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(s.ctaLink); }
                    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
                    else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
                  }}
                  data-testid={`hero-slide-${s.id}`}
                >
                  <img
                    src={s.image}
                    alt={s.imageAlt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    style={{ borderRadius: 'var(--s8-radius-xl)', ...(s.imagePosition ? { objectPosition: s.imagePosition } : {}) }}
                    data-testid={`hero-image-${s.id}`}
                  />

                  {/* Gradient overlay — top and bottom edges darkened, middle clear */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: isActive
                        ? 'linear-gradient(to bottom, rgba(8,10,9,0.88) 0%, rgba(8,10,9,0.3) 22%, transparent 40%, transparent 55%, rgba(8,10,9,0.45) 72%, rgba(8,10,9,0.92) 100%)'
                        : 'linear-gradient(to right, rgba(14,17,16,0.5) 0%, rgba(14,17,16,0.1) 100%)',
                      borderRadius: 'var(--s8-radius-xl)',
                    }}
                  />

                  {/* Title — top-left */}
                  {isActive && (
                    <div
                      className="absolute top-0 left-0 p-5 sm:p-8 lg:p-12"
                      style={{ zIndex: 2 }}
                      key={`title-${current}`}
                    >
                      <h1
                        className="leading-[0.92] hero-slide-text"
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontWeight: 600,
                          fontSynthesis: 'none',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.15,
                          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                          color: 'var(--text-primary)',
                          whiteSpace: 'pre-line',
                          textShadow: '0 2px 12px rgba(0,0,0,0.5)',
                        }}
                        data-testid={`hero-headline-${s.id}`}
                      >
                        {s.headline}
                      </h1>
                    </div>
                  )}

                  {/* Description — bottom */}
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 lg:p-12"
                      style={{ zIndex: 2 }}
                      key={`desc-${current}`}
                    >
                      <p
                        className="hero-slide-text"
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontWeight: 500,
                          fontSize: 'clamp(0.85rem, 1.5vw, 1rem)',
                          lineHeight: 1.5,
                          color: '#F0EBE0',
                          animationDelay: '120ms',
                          textShadow: '0 1px 4px rgba(0,0,0,0.7), 0 2px 12px rgba(0,0,0,0.5)',
                          maxWidth: '640px',
                        }}
                        data-testid={`hero-desc-${s.id}`}
                      >
                        {s.supporting}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Prev / Next arrow buttons — desktop only */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous slide"
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 items-center justify-center transition-all hover:brightness-110"
              style={{
                left: 'clamp(12px, 2vw, 32px)',
                zIndex: 15,
                width: '44px',
                height: '44px',
                borderRadius: '999px',
                background: 'rgba(14, 17, 16, 0.72)',
                border: '1px solid rgba(240, 235, 224, 0.18)',
                color: '#F0EBE0',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                cursor: 'pointer',
              }}
              data-testid="hero-arrow-prev"
            >
              <ChevronLeft style={{ width: '22px', height: '22px' }} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next slide"
              className="hidden md:flex absolute top-1/2 -translate-y-1/2 items-center justify-center transition-all hover:brightness-110"
              style={{
                right: 'clamp(12px, 2vw, 32px)',
                zIndex: 15,
                width: '44px',
                height: '44px',
                borderRadius: '999px',
                background: 'rgba(14, 17, 16, 0.72)',
                border: '1px solid rgba(240, 235, 224, 0.18)',
                color: '#F0EBE0',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
                cursor: 'pointer',
              }}
              data-testid="hero-arrow-next"
            >
              <ChevronRight style={{ width: '22px', height: '22px' }} strokeWidth={2} />
            </button>
          </div>
          {/* Fader nav — replaces dots */}
          <div
            className="flex items-center justify-center py-8 relative landing-container"
            style={{ zIndex: 5 }}
            data-testid="hero-dots"
          >
            <div style={{
              background: '#111410',
              borderRadius: '6px',
              border: '1px solid #252820',
              borderTop: '2px solid #C8923E',
              padding: '1rem 1.5rem 0.75rem',
              position: 'relative',
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.5)',
              maxWidth: '340px',
              margin: '0 auto',
            }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: 'linear-gradient(to right, rgba(0,0,0,0.4), transparent)', borderRadius: '6px 0 0 6px', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '3px', background: 'linear-gradient(to left, rgba(0,0,0,0.4), transparent)', borderRadius: '0 6px 6px 0', pointerEvents: 'none' }} />
              <FaderNav current={current} total={HERO_SLIDES.length} slides={HERO_SLIDES} goTo={goTo} next={next} prev={prev} />
              <div style={{ height: '3px', background: 'linear-gradient(to right, transparent, #1e2018, transparent)', marginTop: '0.5rem', borderRadius: '2px' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent 0%, #0E1110 100%)' }}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT'S NEW — Announcement Strip
// ─────────────────────────────────────────────────────────────────────────────
function WhatsNewStrip() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/whats-new`)
      .then(r => r.json())
      .then(setItems)
      .catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section
      className="relative py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)] overflow-hidden"
      style={{ background: '#0E1110' }}
      data-testid="whats-new-section"
    >
      <div className="landing-container">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--amber)]" />
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '22px', color: 'var(--text-primary)', }}>
              WHAT'S NEW
            </h2>
          </div>
          <Link
            to="/whats-new"
            className="hover:text-[var(--text-primary)] transition-colors flex items-center gap-1"
            style={{ fontFamily: 'var(--font-condensed)', fontWeight: 600, fontSize: '14px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--amber)' }}
            data-testid="whats-new-see-all"
          >
            See all
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <p className="mb-6 sm:mb-8" style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-body)' }}>
          Recent improvements to make gigs smoother and more predictable.
        </p>

        {/* Desktop: grid | Mobile: scrollable with right-edge fade */}
        <div className="relative">
          <div
            className="flex gap-4 sm:grid sm:grid-cols-4 overflow-x-auto pb-2 sm:pb-0 snap-x snap-mandatory sm:snap-none scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {items.slice(0, 4).map((item) => {
              const Icon = ICON_MAP[item.icon] || Sparkles;
              const isNew = item.status === 'new';
              const isFeature = item.status === 'feature';
              return (
                <div
                  key={item.id}
                  className="snap-start shrink-0 w-[78vw] sm:w-auto p-4 sm:p-5 transition-all"
                  style={{
                    background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--bg-surface) 100%)',
                    border: '1px solid var(--border-card)',
                    borderRadius: 'var(--s8-radius-lg)',
                    boxShadow: 'var(--card-shadow)',
                  }}
                  data-testid={`whats-new-card-${item.id}`}
                >
                  <div className="flex items-center gap-2.5 mb-1">
                    <div
                      className="w-8 h-8 flex items-center justify-center shrink-0"
                      style={{ background: 'var(--bg-surface-elevated)', borderRadius: 'var(--s8-radius-sm)', border: '1px solid var(--border-card)' }}
                    >
                      <Icon className="h-3.5 w-3.5 text-[var(--amber)]" />
                    </div>
                    <span
                      className="px-2 py-0.5"
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '12px',
                        fontWeight: 500,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        borderRadius: 'var(--s8-radius-xs)',
                        background: isNew ? 'var(--green-tag-bg)' : isFeature ? 'rgba(100,160,220,0.10)' : 'rgba(200,146,62,0.15)',
                        color: isNew ? 'var(--green-tag-text)' : isFeature ? '#7CB3E0' : 'var(--amber)',
                        border: `1px solid ${isNew ? 'var(--green-dim)' : isFeature ? 'rgba(100,160,220,0.2)' : 'rgba(200,146,62,0.3)'}`,
                      }}
                    >
                      {item.status}
                    </span>
                  </div>
                  <h3 style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '16px', color: 'var(--text-primary)', lineHeight: 1.3, marginBottom: '12px' }}>{item.headline}</h3>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.55 }}>{item.description}</p>
                </div>
              );
            })}
          </div>
          {/* Right-edge fade — mobile only; pointer-events: none so taps pass through */}
          <div
            aria-hidden
            className="sm:hidden absolute top-0 right-0 bottom-0"
            style={{
              width: '24px',
              background: 'linear-gradient(to right, rgba(14,17,16,0) 0%, rgba(14,17,16,0.5) 100%)',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION DIVIDER - Stage Lighting Effect
// ─────────────────────────────────────────────────────────────────────────────
function SectionDivider() {
  return (
    <div className="w-full" style={{
      borderTop: '1px solid',
      borderImage: 'linear-gradient(90deg, transparent 0%, rgba(200, 146, 62, 0.15) 50%, transparent 100%) 1',
    }} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN LANDING PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate('/requests', { replace: true });
  }, [user, navigate]);

  const handleLoadDemo = async () => {
    // Authenticated users have real data — send them to their dashboard, not the demo.
    if (user) { navigate('/requests'); return; }
    setLoadingDemo(true);
    try {
      const res = await seedDemo();
      // Note: do NOT call checkAuth() here. Demo seeding is anonymous —
      // signing visitors in as the demo venue would leak authenticated chrome
      // (avatar / Logout / NEW REQUEST) onto the public Showtime demo.
      navigate(`/bookings/${res.data.booking_id}`);
    } catch {
      toast.error('Could not load demo. Please try again.');
      setLoadingDemo(false);
    }
  };

  return (
    <div className="landing-page min-h-screen overflow-x-hidden" style={{ background: '#0E1110' }}>
      {/* Navigation */}
      <nav 
        className="relative z-50" 
        style={{ 
          background: '#0E1110',
          borderBottom: '1px solid rgba(232, 226, 214, 0.08)',
        }}
      >
        <div className="landing-container h-14 flex items-center justify-between">
          <Link to="/"><Wordmark className="h-[28px]" /></Link>
          
          <div className="flex items-center gap-3 sm:gap-4">
            {/* SHOWTIME — always visible */}
            <button
              onClick={handleLoadDemo}
              disabled={loadingDemo}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium tracking-wide uppercase transition-all hover:bg-[rgba(200,146,62,0.08)]"
              style={{ border: '1px solid #C8923E', color: '#C8923E', fontFamily: "'Barlow', sans-serif", letterSpacing: '0.5px', background: 'transparent' }}
              data-testid="landing-showtime-btn"
            >
              {loadingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Showtime
            </button>
            {/* Desktop-only: Sign In + Get Started (unauthenticated) or Dashboard (authenticated) */}
            {user ? (
              <Link
                to="/requests"
                className="hidden sm:flex h-8 px-4 rounded-[12px] items-center text-[14px] tracking-wide transition-all hover:brightness-110"
                style={{
                  fontFamily: 'var(--font-condensed)',
                  fontWeight: 600,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  background: 'linear-gradient(180deg, #D4A044 0%, #C8923E 100%)',
                  color: '#0E1110',
                  boxShadow: '0 2px 8px rgba(200, 146, 62, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                }}
              >
                DASHBOARD
              </Link>
            ) : (
              <>
                <Link to="/pricing" className="hidden sm:inline text-[12px] text-[var(--text-muted)] hover:text-[var(--text-primary)] tracking-wide transition-colors" style={{ fontFamily: 'var(--font-mono)' }}>
                  PRICING
                </Link>
                <Link to="/login" className="hidden sm:inline text-[12px] text-[var(--text-muted)] hover:text-[var(--text-primary)] tracking-wide transition-colors" style={{ fontFamily: 'var(--font-mono)' }}>
                  SIGN IN
                </Link>
                <Link
                  to="/signup"
                  className="hidden sm:flex h-8 px-4 rounded-[12px] items-center text-[14px] tracking-wide transition-all hover:brightness-110"
                  style={{
                    fontFamily: 'var(--font-condensed)',
                    fontWeight: 600,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    background: 'linear-gradient(180deg, #D4A044 0%, #C8923E 100%)',
                    color: '#0E1110',
                    boxShadow: '0 2px 8px rgba(200, 146, 62, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                  }}
                  data-testid="nav-get-started"
                >
                  GET STARTED
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger — hidden on landing page */}
          {false && <button 
            className="sm:hidden p-2 text-[var(--t1)]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>}
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div
            className="sm:hidden px-4 pb-4 space-y-3"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            {user ? (
              <Link
                to="/requests"
                className="block h-10 rounded-[12px] flex items-center justify-center text-[14px] tracking-wide"
                style={{
                  fontFamily: 'var(--font-condensed)',
                  fontWeight: 600,
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  background: 'linear-gradient(180deg, #D4A044 0%, #C8923E 100%)',
                  color: '#0E1110',
                }}
                onClick={() => setMobileMenuOpen(false)}
              >
                DASHBOARD
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-[12px] text-[var(--text-muted)] tracking-wide py-2"
                  style={{ fontFamily: 'var(--font-mono)' }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  SIGN IN
                </Link>
                <Link
                  to="/signup"
                  className="block h-10 rounded-[12px] flex items-center justify-center text-[14px] tracking-wide"
                  style={{
                    fontFamily: 'var(--font-condensed)',
                    fontWeight: 600,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    background: 'linear-gradient(180deg, #D4A044 0%, #C8923E 100%)',
                    color: '#0E1110',
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  GET STARTED
                </Link>
              </>
            )}
          </div>
        )}
      </nav>

      {/* ═══════════════ HERO CAROUSEL ═══════════════ */}
      <HeroCarousel />

      <SectionDivider />

      {/* ═══════════════ WHAT'S NEW — Announcement Cards ═══════════════ */}
      <WhatsNewStrip />

      <SectionDivider />

      {/* ═══════════════ VISUAL STORY — Equipment Mismatch ═══════════════ */}
      <section 
        className="relative overflow-hidden"
        style={{ background: '#0E1110' }}
        data-testid="visual-story-section"
      >
        {/* Mobile: full-width overlay | Desktop: 2-column side-by-side */}
        <div className="lg:hidden relative w-full story-image-container">
          <img src="/assets/house-kit.webp" alt="Drum kit with a plastic bucket replacing the snare drum" className="w-full h-full object-cover" loading="lazy" style={{ objectPosition: 'center 55%' }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(14, 17, 16, 0.85) 0%, rgba(14, 17, 16, 0.4) 30%, transparent 55%, rgba(14, 17, 16, 0.3) 85%, #0E1110 100%)' }} />
          <div className="absolute top-0 left-0 right-0 px-5 sm:px-8 pt-8 sm:pt-12">
            <h2 className="leading-[0.95]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: 'clamp(2.2rem, 6vw, 4.5rem)', color: 'var(--text-primary)', textShadow: '0 2px 20px rgba(0,0,0,0.8)', }}>"House kit included."</h2>
          </div>
          <div className="absolute bottom-0 right-0 px-5 sm:px-8 pb-5 sm:pb-8 flex flex-col items-end gap-2" style={{ maxWidth: '80%' }}>
            <span className="text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(14, 17, 16, 0.82)', color: '#FF6B6B', border: '1px solid rgba(255, 59, 48, 0.4)', fontWeight: 500 }}>Equipment mismatch</span>
            <p className="text-right" style={{ fontFamily: 'var(--font-body)', fontSize: '14px', lineHeight: 1.45, color: '#F0EBE0', textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>Know exactly what the venue provides<br />Before you accept the gig.</p>
          </div>
        </div>

        {/* Desktop 2-column */}
        <div className="hidden lg:grid lg:grid-cols-2 landing-container py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)] gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <img src="/assets/house-kit.webp" alt="Drum kit with a plastic bucket replacing the snare drum" className="w-full h-full object-cover" loading="lazy" style={{ objectPosition: 'center 55%' }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(14,17,16,0.15) 0%, rgba(14,17,16,0.4) 100%)' }} />
          </div>
          <div className="flex flex-col justify-center gap-5" style={{ maxWidth: '480px' }}>
            <span className="inline-flex self-start text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(255, 59, 48, 0.08)', color: '#FF6B6B', border: '1px solid rgba(255, 59, 48, 0.25)', fontWeight: 500 }}>Equipment mismatch</span>
            <h2 className="leading-[0.95]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '3.5rem', color: 'var(--text-primary)', }}>"House kit included."</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', lineHeight: 1.6, color: 'var(--text-body)', maxWidth: '600px' }}>Know exactly what the venue provides before you accept the gig. No more showing up to a missing snare drum.</p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ═══════════════ HOW IT WORKS — Setlist Card ═══════════════ */}
      <section
        className="relative py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)]"
        style={{ background: '#17200F' }}
      >
        <div className="landing-container relative">
          {/* Section heading + sub-copy */}
          <div className="text-center mb-8 sm:mb-10">
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '28px', color: 'var(--text-primary)', }}>
              HOW THE SHOW GOES ON
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'rgba(240,235,224,0.38)', marginTop: '10px' }}>
              Every gig that goes smoothly started with a shared record.
            </p>
          </div>

          {/* Setlist card */}
          <div style={{
            maxWidth: '560px',
            margin: '0 auto',
            background: '#F0EDE4',
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E\")",
            border: '1px solid rgba(200,146,62,0.18)',
            borderRadius: '3px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.22), 0 6px 24px rgba(0,0,0,0.16), 0 0 40px rgba(200,146,62,0.08)',
            padding: '22px 26px 18px',
          }}>
            {/* Card header: wordmark + rule */}
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '12px', color: '#1c1c1c', marginBottom: '9px' }}>
              STAGE8
            </div>
            <div style={{ borderBottom: '1px solid rgba(0,0,0,0.16)', marginBottom: '4px' }} />

            {/* Track rows */}
            {[
              { num: '01', title: 'POST THE GIG',         desc: 'Instrument, fee, date, location. Structured fields, no guesswork.',              Icon: Music },
              { num: '02', title: 'GET MATCHED',           desc: 'Musicians and crew matched on rate, genre, and travel range.',                  Icon: Users },
              { num: '03', title: 'CONFIRM THE DETAILS',   desc: 'Both sides confirm. One shared record, documented before doors open.',          Icon: CheckSquare },
              { num: '04', title: 'SHOWTIME',              desc: 'Run of show, key contacts, readiness checklist. All in one place.',             Icon: Zap },
            ].map(({ num, title, desc, Icon }, i, arr) => (
              <React.Fragment key={num}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '26px 1fr 22px',
                  gap: '0 14px',
                  alignItems: 'start',
                  padding: '13px 0',
                }}>
                  {/* Track number */}
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#C8923E', letterSpacing: '0.5px', paddingTop: '3px' }}>
                    {num}
                  </div>
                  {/* Title + descriptor */}
                  <div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '16px', color: '#1c1c1c', marginBottom: '4px' }}>
                      {title}
                    </div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: '#888', lineHeight: 1.45 }}>
                      {desc}
                    </div>
                  </div>
                  {/* Icon */}
                  <div style={{ paddingTop: '3px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Icon size={13} color="#C8923E" strokeWidth={1.5} />
                  </div>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ borderBottom: '1px solid rgba(42,42,42,0.18)' }} />
                )}
              </React.Fragment>
            ))}

            {/* Card footer */}
            <div style={{ borderTop: '1px solid rgba(0,0,0,0.13)', marginTop: '12px', paddingTop: '11px', textAlign: 'center' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#999', letterSpacing: '2.5px', textTransform: 'uppercase' }}>
                DOORS OPEN AT SHOWTIME
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <SectionDivider />

      {/* ═══════════════ VISUAL STORY — The Digital Handshake ═══════════════ */}
      <section 
        className="relative overflow-hidden"
        style={{ background: '#0E1110' }}
        data-testid="visual-story-handshake-section"
      >
        {/* Mobile: full-width overlay */}
        <div className="lg:hidden relative w-full story-image-container">
          <img src="/assets/handshake.webp" alt="Two hands meeting in a digital handshake — confirming gig details before the show" className="w-full h-full object-cover" loading="lazy" style={{ objectPosition: 'center center' }} />
          {/* Stronger bottom veil — body copy is longer in this section, so darken lower 50% for legibility */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(14, 17, 16, 0.85) 0%, rgba(14, 17, 16, 0.45) 22%, rgba(14, 17, 16, 0.2) 42%, rgba(14, 17, 16, 0.72) 62%, rgba(14, 17, 16, 0.92) 100%)' }} />
          <div className="absolute top-0 left-0 right-0 px-5 sm:px-8 pt-8 sm:pt-12">
            <h2 className="leading-[0.95] sm:text-right" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: 'clamp(2.2rem, 6vw, 4.5rem)', color: 'var(--text-primary)', textShadow: '0 2px 20px rgba(0,0,0,0.8)', }}>Expectation Management</h2>
          </div>
          <div className="absolute bottom-0 right-0 px-5 sm:px-8 pb-5 sm:pb-8 flex flex-col items-end gap-2" style={{ maxWidth: '80%' }}>
            <span className="text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(14, 17, 16, 0.82)', color: '#D4A044', border: '1px solid rgba(200, 146, 62, 0.4)', fontWeight: 500 }}>Soft Digital Handshake</span>
            <p className="text-right" style={{ fontFamily: 'var(--font-body)', fontSize: '14px', lineHeight: 1.45, color: '#F0EBE0', textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>Every gig starts here. One shared record of fee, timing, gear, contacts, and logistics, confirmed by both sides before showtime.</p>
          </div>
        </div>

        {/* Desktop 2-column (reversed: text left, image right) */}
        <div className="hidden lg:grid lg:grid-cols-2 landing-container py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)] gap-12 items-center">
          <div className="flex flex-col justify-center gap-5" style={{ maxWidth: '480px' }}>
            <span className="inline-flex self-start text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(200, 146, 62, 0.08)', color: '#D4A044', border: '1px solid rgba(200, 146, 62, 0.25)', fontWeight: 500 }}>Soft Digital Handshake</span>
            <h2 className="leading-[0.95]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '3.5rem', color: 'var(--text-primary)', }}>Expectation Management</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', lineHeight: 1.6, color: 'var(--text-body)', maxWidth: '600px' }}>Every gig starts here. One shared record of fee, timing, gear, contacts, and logistics, confirmed by both sides before showtime.</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <img src="/assets/handshake.webp" alt="Two hands meeting in a digital handshake — confirming gig details before the show" className="w-full h-full object-cover" loading="lazy" style={{ objectPosition: 'center center' }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(14,17,16,0.15) 0%, rgba(14,17,16,0.4) 100%)' }} />
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <SectionDivider />

      {/* ═══════════════ DUAL AUDIENCE ═══════════════ */}
      <section
        className="relative py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)]"
        style={{ background: '#0E1110' }}
      >
        <div className="landing-container relative">
          {/* Section header — centered, editorial (matches the rest of the page) */}
          <div className="flex flex-col items-center text-center gap-4 mb-12 lg:mb-16">
            <span className="text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(200, 146, 62, 0.08)', color: '#D4A044', border: '1px solid rgba(200, 146, 62, 0.25)', fontWeight: 500 }}>
              Whichever side you're on
            </span>
            <h2 className="leading-[0.95]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'var(--text-primary)', }}>
              Two Sides, One Handshake
            </h2>
          </div>

          {/* Two type-driven columns split by a hairline — no cards */}
          <div className="grid sm:grid-cols-2 gap-10 sm:gap-0 max-w-4xl mx-auto">
            {/* Venues */}
            <div className="sm:pr-12 lg:pr-16">
              <h3 style={{ fontFamily: 'var(--font-condensed)', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>
                For Venues
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: '16px', color: 'var(--text-body)', lineHeight: 1.6, marginBottom: '20px' }}>
                Post what you need, when you need it. We surface verified local talent who can actually get there.
              </p>
              <Link to="/venues/requests/new" data-testid="audience-venues" className="group inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" style={{ fontFamily: 'var(--font-condensed)', fontWeight: 600, fontSize: '14px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--amber)' }}>
                Post a task <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {/* Talent */}
            <div className="sm:pl-12 lg:pl-16 sm:border-l" style={{ borderColor: 'var(--border-card)' }}>
              <h3 style={{ fontFamily: 'var(--font-condensed)', fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '12px' }}>
                For Musicians &amp; Crew
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: '16px', color: 'var(--text-body)', lineHeight: 1.6, marginBottom: '20px' }}>
                Get matched to gigs that fit your skills, gear, and location. No more cold outreach.
              </p>
              <Link to="/requests" data-testid="audience-talent" className="group inline-flex items-center gap-1.5 hover:gap-2.5 transition-all" style={{ fontFamily: 'var(--font-condensed)', fontWeight: 600, fontSize: '14px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--amber)' }}>
                Browse open gigs <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <SectionDivider />

      {/* ═══════════════ VISUAL STORY — Genre Mismatch ═══════════════ */}
      <section 
        className="relative overflow-hidden"
        style={{ background: '#0E1110' }}
        data-testid="visual-story-genre-section"
      >
        {/* Mobile: full-width overlay */}
        <div className="lg:hidden relative w-full story-image-container">
          <img src="/assets/genre-mismatch.webp" alt="Banjo player in overalls and straw hat on stage with a heavy metal band" className="w-full h-full object-cover" loading="lazy" style={{ objectPosition: 'center 30%' }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(14, 17, 16, 0.85) 0%, rgba(14, 17, 16, 0.4) 30%, transparent 55%, rgba(14, 17, 16, 0.3) 85%, #0E1110 100%)' }} />
          <div className="absolute top-0 left-0 right-0 px-5 sm:px-8 pt-8 sm:pt-12">
            <h2 className="leading-[0.95]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: 'clamp(2.2rem, 6vw, 4.5rem)', color: 'var(--text-primary)', textShadow: '0 2px 20px rgba(0,0,0,0.8)', }}>"Versatile player needed."</h2>
          </div>
          <div className="absolute bottom-0 right-0 px-5 sm:px-8 pb-5 sm:pb-8 flex flex-col items-end gap-2" style={{ maxWidth: '80%' }}>
            <span className="text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(14, 17, 16, 0.82)', color: '#D4A0E8', border: '1px solid rgba(180, 80, 200, 0.4)', fontWeight: 500 }}>Genre mismatch</span>
            <p className="text-right" style={{ fontFamily: 'var(--font-body)', fontSize: '14px', lineHeight: 1.45, color: '#F0EBE0', textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>Taste the vibe<br />Before you show up.</p>
          </div>
        </div>

        {/* Desktop 2-column (image left, text right) */}
        <div className="hidden lg:grid lg:grid-cols-2 landing-container py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)] gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <img src="/assets/genre-mismatch.webp" alt="Banjo player in overalls and straw hat on stage with a heavy metal band" className="w-full h-full object-cover" loading="lazy" style={{ objectPosition: 'center 30%' }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(14,17,16,0.15) 0%, rgba(14,17,16,0.4) 100%)' }} />
          </div>
          <div className="flex flex-col justify-center gap-5" style={{ maxWidth: '480px' }}>
            <span className="inline-flex self-start text-[12px] tracking-widest uppercase px-3 py-1.5" style={{ fontFamily: 'var(--font-mono)', borderRadius: '6px', background: 'rgba(180, 80, 200, 0.08)', color: '#D4A0E8', border: '1px solid rgba(180, 80, 200, 0.25)', fontWeight: 500 }}>Genre mismatch</span>
            <h2 className="leading-[0.95]" style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '3.5rem', color: 'var(--text-primary)', }}>"Versatile player needed."</h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', lineHeight: 1.6, color: 'var(--text-body)', maxWidth: '600px' }}>Taste the vibe before you show up. Genre tags and alignment scores help you find gigs that match your style.</p>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* ═══════════════ FOOTER CTA — Late ask ═══════════════ */}
      <section 
        className="relative py-[var(--s8-section-py)] lg:py-[var(--s8-section-py-lg)] overflow-hidden"
        style={{ background: '#0E1110' }}
      >
        {/* Central amber glow */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] sm:w-[700px] h-[280px] sm:h-[350px] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse, rgba(200, 146, 62, 0.10) 0%, transparent 55%)',
          }}
        />
        
        <div className="landing-container text-center relative">
          <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '28px', color: 'var(--text-primary)', marginBottom: '16px' }}>
            READY TO FIND LOCAL TALENT?
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--text-body)', marginBottom: '40px' }}>
            Join the roster of professionals who value reliability over chaos.
          </p>
          <Link 
            to="/signup" 
            className="inline-flex h-12 sm:h-14 px-8 sm:px-10 rounded-[12px] items-center gap-2 transition-all hover:brightness-110"
            style={{ 
              fontFamily: 'var(--font-condensed)',
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              background: 'linear-gradient(180deg, #D4A044 0%, #C8923E 100%)',
              color: '#0E1110',
              boxShadow: '0 2px 8px rgba(200, 146, 62, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
            }}
            data-testid="cta-create-account"
          >
            CREATE ACCOUNT
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="py-8 sm:py-10"
        style={{ 
          background: '#0E1110',
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        <div className="landing-container">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <Wordmark className="h-[20px]" style={{ opacity: 0.6 }} />
              <span className="hidden sm:block w-px h-4" style={{ background: 'var(--border-subtle)' }}></span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.6', textAlign: 'left', display: 'block' }}>Vitamin for Venues<br />Oxygen for Musicians</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/how-it-works" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)' }} className="hover:text-[var(--text-primary)] tracking-wide transition-colors">
                HOW IT WORKS
              </Link>
              <Link to="/requests" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)' }} className="hover:text-[var(--text-primary)] tracking-wide transition-colors">
                BROWSE TASKS
              </Link>
              <a href="mailto:hello@stage8.io" style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)' }} className="hover:text-[var(--text-primary)] tracking-wide transition-colors">
                CONTACT
              </a>
            </div>
          </div>
          <div className="mt-6 pt-4 text-center sm:text-left" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)' }}>
              © 2026 STAGE8.IO
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
